import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo } from "react"
import type { ProductListItemDTO } from "../../../../api/productList"
import {
  type TechnologyResponseDTO,
  TechnologyService,
} from "../../../../client"
import AddTechnology, {
  type TechnologyFormInitialValues,
} from "../../../../components/WMS/Technologies/AddTechnology"

export const Route = createFileRoute("/_layout/WMS/technologies/create")({
  validateSearch: (search: Record<string, unknown>) => ({
    clone_from:
      typeof search.clone_from === "string" ? search.clone_from : undefined,
    mode:
      search.mode === "variant" || search.mode === "version"
        ? search.mode
        : undefined,
  }),
  component: CreateTechnologyPage,
})

/** Normalizes copied technology codes to the same uppercase format as the form. */
const normalizeTechnologyCode = (value: string) =>
  value.trim().toLocaleUpperCase("pl-PL")

/** Converts a technology detail response into editable create-form defaults. */
const buildVariantInitialValues = (
  technology: TechnologyResponseDTO,
): TechnologyFormInitialValues => {
  const toProduct = (product: any): ProductListItemDTO => ({
    id: product.id,
    manufacturer: product.manufacturer ?? "",
    sku: product.sku ?? "",
    name: product.name ?? "",
    EAN: product.EAN ?? product.ean ?? "",
    unit: product.unit ?? "",
    is_active: product.is_active ?? true,
    is_service: product.is_service ?? false,
    acronym: product.acronym ?? "",
    created_at: product.created_at ?? "",
  })

  return {
    code: `${normalizeTechnologyCode(technology.code ?? "")}-KOPIA`,
    name: `${technology.name ?? ""} - kopia`,
    description: technology.description ?? "",
    version: "1",
    isActive: technology.is_active ?? true,
    requiresExpirationDate: technology.requires_expiration_date ?? false,
    validityDays: technology.validity_days
      ? String(technology.validity_days)
      : "",
    inputs: (technology.inputs ?? []).map((row) => ({
      product: toProduct((row as any).product ?? {}),
      qty: `${row.qty ?? ""}`,
      loss_percent: `${row.loss_percent ?? ""}`,
      service_cost_netto: `${row.service_cost_netto ?? ""}`,
      cost_note: row.cost_note ?? "",
      substitutes: (row.substitutes ?? []).map((sub) => ({
        product: toProduct((sub as any).product ?? {}),
        qty: `${sub.qty ?? ""}`,
        loss_percent: `${sub.loss_percent ?? ""}`,
        service_cost_netto: `${sub.service_cost_netto ?? ""}`,
        priority: `${sub.priority ?? "1"}`,
        note: sub.note ?? "",
      })),
    })),
    outputs: (technology.outputs ?? []).map((row) => ({
      product: toProduct((row as any).product ?? {}),
      qty: `${row.qty ?? ""}`,
      is_main: !!row.is_main,
    })),
  }
}

function CreateTechnologyPage() {
  const navigate = useNavigate()
  const { clone_from: cloneFrom, mode } = Route.useSearch()
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const {
    data: sourceTechnology,
    isError: isSourceError,
    isPending: isSourcePending,
    error: sourceError,
  } = useQuery({
    queryKey: ["technologies", "clone-source", cloneFrom],
    queryFn: () => TechnologyService.getTechnologyByID(cloneFrom ?? ""),
    enabled: !!cloneFrom,
  })
  const initialValues = useMemo(
    () =>
      sourceTechnology
        ? mode === "version"
          ? {
              ...buildVariantInitialValues(sourceTechnology),
              code: normalizeTechnologyCode(sourceTechnology.code ?? ""),
              name: sourceTechnology.name ?? "",
              version: `${(sourceTechnology.version ?? 1) + 1}`,
            }
          : buildVariantInitialValues(sourceTechnology)
        : null,
    [mode, sourceTechnology],
  )

  return (
    <Container maxW="9xl" py={4}>
      <VStack spacing={4} align="stretch">
        <Box
          bgGradient="linear(to-r, teal.600, green.600)"
          color="white"
          borderRadius="2xl"
          px={{ base: 5, md: 8 }}
          py={{ base: 5, md: 7 }}
          boxShadow="lg"
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <Box>
              <Heading size="lg">
                {cloneFrom
                  ? mode === "version"
                    ? "Stworzyć nową wersję technologii"
                    : "Stworzyć wariant technologii"
                  : "Stworzyć technologię"}
              </Heading>
              <Text mt={1} opacity={0.95}>
                {cloneFrom
                  ? mode === "version"
                    ? "Nowa rewizja zachowa ten sam kod technologii i zapisze się jako kolejna wersja."
                    : "Skopiowana definicja technologii jest gotowa do edycji przed zapisem."
                  : "Definicja technologii produkcji, wejść, wyjść i kosztów."}
              </Text>
            </Box>
            <Badge
              bg="whiteAlpha.250"
              color="white"
              borderRadius="full"
              px={3}
              py={1}
            >
              Tworzenie technologii
            </Badge>
          </Flex>
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={{ base: 4, md: 5 }}
          boxShadow="sm"
        >
          {cloneFrom && isSourcePending ? (
            <Flex justify="center" align="center" minH="240px">
              <Spinner size="xl" color="main.accent" />
            </Flex>
          ) : cloneFrom && isSourceError ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Nie udało się skopiować technologii:{" "}
              {(sourceError as any)?.message ?? "Błąd"}
            </Alert>
          ) : (
            <AddTechnology
              initialValues={initialValues}
              formMode={
                mode === "version"
                  ? "version"
                  : cloneFrom
                    ? "variant"
                    : "create"
              }
              isVersionLocked
              onSuccess={() =>
                navigate({
                  to: "/WMS/technologies/technologies-list",
                  search: { page: 1, page_size: 25, status: "active" },
                })
              }
              onCancel={() =>
                navigate({
                  to: "/WMS/technologies/technologies-list",
                  search: { page: 1, page_size: 25, status: "active" },
                })
              }
            />
          )}
        </Box>
      </VStack>
    </Container>
  )
}
